import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Mail, Phone, User, MessageSquare } from 'lucide-react';

const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedContact, setExpandedContact] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/contacts', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setContacts(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load contacts.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/contacts/${contactId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to update');

      toast({
        title: "Success!",
        description: "Contact status updated.",
      });
      
      fetchContacts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update contact status.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#f59e0b',
      read: '#3b82f6',
      responded: '#22c55e'
    };
    return colors[status] || '#736c64';
  };

  if (loading) {
    return <div className="text-center py-8">Loading messages...</div>;
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="h-16 w-16 mx-auto mb-4" style={{ color: '#736c64' }} />
        <h3 className="text-xl font-semibold mb-2" style={{ color: '#4a2c1a' }}>No messages yet</h3>
        <p style={{ color: '#736c64' }}>Contact form submissions will appear here.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold mb-6" style={{ color: '#4a2c1a' }}>
        Customer Messages
      </h2>

      <div className="space-y-4">
        {contacts.map((contact) => (
          <div key={contact.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#FF8C1920' }}
                  >
                    <User className="h-6 w-6" style={{ color: '#FF8C19' }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: '#4a2c1a' }}>
                      {contact.name}
                    </h3>
                    <p className="text-sm" style={{ color: '#736c64' }}>
                      {new Date(contact.createdAt).toLocaleDateString()} at {new Date(contact.createdAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span
                    className="px-4 py-2 rounded-full text-sm font-semibold text-white"
                    style={{ backgroundColor: getStatusColor(contact.status) }}
                  >
                    {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                  </span>
                  <Button
                    onClick={() => setExpandedContact(expandedContact === contact.id ? null : contact.id)}
                    variant="outline"
                  >
                    {expandedContact === contact.id ? 'Hide' : 'View'}
                  </Button>
                </div>
              </div>

              {expandedContact === contact.id && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: '#e5d5c0' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-semibold mb-3" style={{ color: '#4a2c1a' }}>
                        Contact Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p className="flex items-center" style={{ color: '#736c64' }}>
                          <Mail className="h-4 w-4 mr-2" />
                          <a href={`mailto:${contact.email}`} className="hover:underline">
                            {contact.email}
                          </a>
                        </p>
                        <p className="flex items-center" style={{ color: '#736c64' }}>
                          <Phone className="h-4 w-4 mr-2" />
                          <a href={`tel:${contact.phone}`} className="hover:underline">
                            {contact.phone}
                          </a>
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3" style={{ color: '#4a2c1a' }}>
                        Message
                      </h4>
                      <p className="text-sm p-4 rounded" style={{ backgroundColor: '#f7f5f2', color: '#4a2c1a' }}>
                        {contact.message}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#4a2c1a' }}>
                      Update Status
                    </label>
                    <Select value={contact.status} onValueChange={(value) => updateContactStatus(contact.id, value)}>
                      <SelectTrigger className="w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="read">Read</SelectItem>
                        <SelectItem value="responded">Responded</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContactsTab;
